/**
 * Mapbox Streets v8 — invisible query layer infrastructure.
 *
 * Adds `mapbox-streets-v8` as a vector source with near-invisible layers
 * so we can call `queryRenderedFeatures` to extract spatial context
 * (POIs, roads, landuse, buildings, transit, place labels).
 *
 * The tiles are already loaded by Mapbox Standard internally —
 * browser HTTP cache prevents double downloads.
 */

import type { Map as MapboxMap, PointLike } from "mapbox-gl";

// ── Types ──

export interface MapContext {
  nearby_pois?: { name: string; type?: string; class?: string; sizerank?: number; brand?: string }[];
  roads?: { name: string; class?: string }[];
  landuse?: string[];
  buildings?: { type: string; max_height?: number; count: number }[];
  transit?: { name: string; mode?: string }[];
  place?: { name: string; type?: string };
}

// ── Source & layer config ──

const STREETS_SOURCE = "streets-v8";

const QUERY_LAYERS = [
  { id: "_q-poi", type: "circle" as const, sourceLayer: "poi_label", paint: { "circle-radius": 1, "circle-opacity": 0.01 } },
  { id: "_q-road", type: "line" as const, sourceLayer: "road", paint: { "line-opacity": 0.01 } },
  { id: "_q-landuse", type: "fill" as const, sourceLayer: "landuse", paint: { "fill-opacity": 0.01 } },
  { id: "_q-building", type: "fill" as const, sourceLayer: "building", paint: { "fill-opacity": 0.01 } },
  { id: "_q-transit", type: "circle" as const, sourceLayer: "transit_stop_label", paint: { "circle-radius": 1, "circle-opacity": 0.01 } },
  { id: "_q-place", type: "circle" as const, sourceLayer: "place_label", paint: { "circle-radius": 1, "circle-opacity": 0.01 } },
] as const;

// ── Source management ──

export function addStreetsSource(map: MapboxMap): void {
  try {
    if (map.getSource(STREETS_SOURCE)) return;

    map.addSource(STREETS_SOURCE, {
      type: "vector",
      url: "mapbox://mapbox.mapbox-streets-v8",
    });

    for (const layer of QUERY_LAYERS) {
      if (!map.getLayer(layer.id)) {
        map.addLayer({
          id: layer.id,
          type: layer.type,
          source: STREETS_SOURCE,
          "source-layer": layer.sourceLayer,
          paint: layer.paint as Record<string, unknown>,
        });
      }
    }
  } catch (err) {
    console.debug("[Ortvis] Failed to add Streets v8 source:", err);
  }
}

export function removeStreetsSource(map: MapboxMap): void {
  for (const layer of [...QUERY_LAYERS].reverse()) {
    try {
      if (map.getLayer(layer.id)) map.removeLayer(layer.id);
    } catch { /* layer may already be removed */ }
  }
  try {
    if (map.getSource(STREETS_SOURCE)) map.removeSource(STREETS_SOURCE);
  } catch { /* source may already be removed */ }
}

// ── Context extraction ──

/**
 * Extract spatial context from Streets v8 at a map point.
 * Returns null if zoom < 12 (tiles not loaded) or no data found.
 */
export function extractMapContext(
  map: MapboxMap,
  center: [number, number],
  radiusPx = 100,
  include?: string[],
): MapContext | null {
  const zoom = map.getZoom();
  if (zoom < 12) return null;

  const point = map.project(center);
  // Wider radius for sparse layers (POI, transit) — catches transit stations ~400m away
  const wideRadiusPx = Math.max(radiusPx * 2, 200);
  const bbox: [PointLike, PointLike] = [
    [point.x - radiusPx, point.y - radiusPx],
    [point.x + radiusPx, point.y + radiusPx],
  ];
  const wideBbox: [PointLike, PointLike] = [
    [point.x - wideRadiusPx, point.y - wideRadiusPx],
    [point.x + wideRadiusPx, point.y + wideRadiusPx],
  ];

  const shouldInclude = (layer: string) => !include || include.includes(layer);
  const ctx: MapContext = {};

  // POIs — include sizerank (0-16, lower=more important) and brand (chain identification)
  if (shouldInclude("poi") && map.getLayer("_q-poi")) {
    const seen = new Set<string>();
    const pois: MapContext["nearby_pois"] = [];
    for (const f of map.queryRenderedFeatures(wideBbox, { layers: ["_q-poi"] }).slice(0, 15)) {
      const name = f.properties?.name as string | undefined;
      if (!name || seen.has(name)) continue;
      seen.add(name);
      const poi: NonNullable<MapContext["nearby_pois"]>[number] = { name };
      if (f.properties?.type) poi.type = f.properties.type as string;
      if (f.properties?.class) poi.class = f.properties.class as string;
      if (f.properties?.sizerank != null) poi.sizerank = f.properties.sizerank as number;
      if (f.properties?.brand) poi.brand = f.properties.brand as string;
      pois.push(poi);
    }
    ctx.nearby_pois = pois;
  }

  // Roads — structured with class for hierarchy (primary/secondary/street/pedestrian/etc.)
  if (shouldInclude("roads") && map.getLayer("_q-road")) {
    const roadMap = new Map<string, string | undefined>();
    for (const f of map.queryRenderedFeatures(bbox, { layers: ["_q-road"] })) {
      const name = f.properties?.name as string | undefined;
      if (!name || roadMap.has(name)) continue;
      roadMap.set(name, f.properties?.class as string | undefined);
    }
    const roads: NonNullable<MapContext["roads"]> = [];
    for (const [name, roadClass] of [...roadMap].slice(0, 8)) {
      const entry: { name: string; class?: string } = { name };
      if (roadClass) entry.class = roadClass;
      roads.push(entry);
    }
    ctx.roads = roads;
  }

  // Land use
  if (shouldInclude("landuse") && map.getLayer("_q-landuse")) {
    ctx.landuse = [
      ...new Set(
        map
          .queryRenderedFeatures(bbox, { layers: ["_q-landuse"] })
          .map((f) => f.properties?.class as string | undefined)
          .filter(Boolean) as string[],
      ),
    ];
  }

  // Buildings — aggregate by type with count + max height for density/scale signal
  if (shouldInclude("buildings") && map.getLayer("_q-building")) {
    const byType = new Map<string, { count: number; maxHeight?: number }>();
    for (const f of map.queryRenderedFeatures(bbox, { layers: ["_q-building"] }).slice(0, 50)) {
      const type = f.properties?.type as string | undefined;
      if (!type) continue;
      const height = f.properties?.height as number | undefined;
      const existing = byType.get(type);
      if (existing) {
        existing.count++;
        if (height && (!existing.maxHeight || height > existing.maxHeight)) existing.maxHeight = height;
      } else {
        byType.set(type, { count: 1, maxHeight: height || undefined });
      }
    }
    ctx.buildings = [...byType.entries()].map(([type, { count, maxHeight }]) => ({
      type, count, max_height: maxHeight,
    }));
  }

  // Transit
  if (shouldInclude("transit") && map.getLayer("_q-transit")) {
    const seen = new Set<string>();
    const transit: MapContext["transit"] = [];
    for (const f of map.queryRenderedFeatures(wideBbox, { layers: ["_q-transit"] }).slice(0, 10)) {
      const name = f.properties?.name as string | undefined;
      if (!name) continue;
      const mode = f.properties?.mode as string | undefined;
      const key = `${name}|${mode}`;
      if (seen.has(key)) continue;
      seen.add(key);
      transit.push({ name, mode });
    }
    ctx.transit = transit;
  }

  // Place label
  if (shouldInclude("places") && map.getLayer("_q-place")) {
    const place = map.queryRenderedFeatures(bbox, { layers: ["_q-place"] })[0];
    if (place?.properties?.name) {
      ctx.place = {
        name: place.properties.name as string,
        type: place.properties.type as string | undefined,
      };
    }
  }

  // Strip empty arrays
  for (const [k, v] of Object.entries(ctx)) {
    if (Array.isArray(v) && v.length === 0) {
      delete ctx[k as keyof MapContext];
    }
  }

  return Object.keys(ctx).length > 0 ? ctx : null;
}
